import initStatisticAnimation from "../StatisticAnimation/initStatisticAnimation.js";
import { equalHeight } from "../../common/equalHeight.js";

export default function initAdmissionsTabs() {
  const admissionsTabs = document.querySelectorAll(".js-admissions-tabs");
  const matchesNavigation = window.innerWidth > 1200;

  if (admissionsTabs.length > 0) {
    document.documentElement.classList.add("--add-scroll-padding");

    admissionsTabs.forEach((tabs, i) => {
      const tabsContainer = tabs.querySelector('[role="tablist"]');
      const tabsList = tabs.querySelectorAll('[role="tab"]');
      const tabPanelsList = tabs.querySelectorAll('[role="tabpanel"]');
      const navButtonLeft = tabs.querySelector(
        ".js-admissions-tabs__nav-button-left",
      );
      const navButtonRight = tabs.querySelector(
        ".js-admissions-tabs__nav-button-right",
      );

      // Update the left offset of the tabs.
      // This is needed because they are in a bleed container,
      // but the design calls for their starting point to act as if they're in a Container100.
      setOffset(tabs, tabsContainer, matchesNavigation);

      // If there is no overflow of tabs, remove the left/right navigation buttons.
      if (tabsContainer.scrollWidth <= window.innerWidth) {
        navButtonLeft.remove();
        navButtonRight.remove();
      }

      // Functions to re-run on window resize
      window.onresize = function () {
        setOffset(tabs, tabsContainer, matchesNavigation);
      };

      // Update the href value of each tab, and add a click handler
      tabsList.forEach((tab) => {
        const tabHref = tab
          .getAttribute("href")
          .slice(1)
          .replace(/[^a-zA-Z0-9]/g, "");

        tab.setAttribute("href", "#" + tabHref);
        tab.setAttribute("aria-controls", tabHref);

        tab.addEventListener("click", function () {
          changeTabs(tab);
        });
      });

      // Update the ID of each panel to match the tab href values
      tabPanelsList.forEach((panel) => {
        const panelId = panel.getAttribute("id").replace(/[^a-zA-Z0-9]/g, "");

        panel.setAttribute("id", panelId);
      });

      // Add click handlers to the left/right navigation buttons
      navButtonLeft.addEventListener("click", function () {
        const currentTab = tabs.querySelector('[aria-selected="true"]');
        const prevTab = currentTab.previousElementSibling;

        // If we're at the start, move to the last tab
        if (prevTab === null) {
          changeTabs(tabsList[tabsList.length - 1]);
          tabsContainer.scrollLeft = tabsContainer.scrollWidth;
        } else {
          // Otherwise, go to the previous tab
          changeTabs(prevTab);
          // sideScroll(element, direction, speed, distance, step)
          sideScroll(
            tabsContainer,
            "left",
            15,
            currentTab.offsetWidth + 30,
            10,
          );
        }
      });

      navButtonRight.addEventListener("click", function () {
        const currentTab = tabs.querySelector('[aria-selected="true"]');
        const nextTab = currentTab.nextElementSibling;

        // If we're at the end, move to the first tab
        if (nextTab === null) {
          changeTabs(tabsList[0]);
          tabsContainer.scrollLeft = 0;
        } else {
          // Otherwise, go to the next tab
          changeTabs(nextTab);
          // sideScroll(element, direction, speed, distance, step)
          sideScroll(
            tabsContainer,
            "right",
            15,
            currentTab.offsetWidth + 30,
            10,
          );
        }
      });

      // First, check if the link has a hash value at the end;
      // if so, manually select that tab + its corresponding panel.
      const hash = window.location.hash;
      if (hash) {
        tabsList.forEach((tab) => {
          const href = tab.getAttribute("href");

          if (href === hash) {
            changeTabs(tab);
          }
        });
      } else {
        // If there is not a hash value in the link,
        // select the first tab + its corresponding panel.
        // Do NOT add it to the URL, otherwise the page will jump to this component.
        tabsList[0].setAttribute("tabindex", -1);
        tabsList[0].setAttribute("aria-selected", true);
        tabsList[0].classList.add("--active");

        // Get the panel whose ID matches the selected tab's href
        const currentPanel = Array.from(tabPanelsList).find(
          (panel) => panel.id === tabsList[0].getAttribute("href").slice(1),
        );

        // Hide all tab panels
        tabPanelsList.forEach((panel) => {
          panel.setAttribute("hidden", true);
        });

        // Show the current panel
        currentPanel.removeAttribute("hidden");

        const statisticAnimationEls = currentPanel.querySelectorAll(
          ".js-statistic-animation",
        );
        runStatisticAnimations(statisticAnimationEls);
      }
    });
  }
}

function setOffset(tabs, tabsContainer, matchesNavigation) {
  const eyebrow = tabs.querySelector(".admissions-tabs__eyebrow");

  if (eyebrow) {
    const eyebrowLeftOffset = eyebrow.offsetLeft;

    if (eyebrowLeftOffset > 0) {
      tabsContainer.style.paddingLeft = eyebrowLeftOffset + "px";
      tabsContainer.style.paddingRight = eyebrowLeftOffset + "px";
    } else {
      tabsContainer.style.paddingLeft = "35px";
      tabsContainer.style.paddingRight = "35px";
    }
  } else {
    if (matchesNavigation) {
      const topContent = document.querySelector(
        ".admissions-tabs__top-content",
      );
      tabsContainer.style.paddingLeft =
        (window.innerWidth - topContent.clientWidth) / 2 + "px";
      tabsContainer.style.paddingRight =
        (window.innerWidth - topContent.clientWidth) / 2 + "px";
    } else {
      tabsContainer.style.paddingLeft = "35px";
      tabsContainer.style.paddingRight = "35px";
    }
  }
}

function changeTabs(tab) {
  const tabsContainer = tab.parentNode;
  const tabsGrandparentContainer = tabsContainer.parentNode;

  // Add hash value to URL
  const hash = tab.getAttribute("href");
  window.location = hash;

  // Remove the currently selected tab
  tabsContainer.querySelectorAll('[aria-selected="true"]').forEach((tab) => {
    tab.classList.remove("--active");
    tab.setAttribute("aria-selected", false);
  });

  // Set this tab as selected
  tab.classList.add("--active");
  tab.setAttribute("aria-selected", true);

  // Hide all tab panels
  tabsGrandparentContainer
    .querySelectorAll('[role="tabpanel"]')
    .forEach((panel) => {
      panel.setAttribute("hidden", true);
    });

  // Show the current panel
  tabsGrandparentContainer.parentNode
    .querySelector(tab.getAttribute("href"))
    .removeAttribute("hidden");

  // Re-run any statistic animations when the tab opens
  const statisticAnimationEls = tabsGrandparentContainer.parentNode
    .querySelector(tab.getAttribute("href"))
    .querySelectorAll(".js-statistic-animation");

  runStatisticAnimations(statisticAnimationEls);
}

function sideScroll(element, direction, speed, distance, step) {
  let scrollAmount = 0;
  var slideTimer = setInterval(function () {
    if (direction === "left") {
      element.scrollLeft -= step;
    } else {
      element.scrollLeft += step;
    }
    scrollAmount += step;
    if (scrollAmount >= distance) {
      window.clearInterval(slideTimer);
    }
  }, speed);
}

function runStatisticAnimations(animations) {
  if (
    document.getElementsByClassName("statistic-animation__description").length
  ) {
    equalHeight(".statistic-animation__description");
  }

  animations.forEach((el, j) => {
    initStatisticAnimation(el);
  });
}
